import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { listProducts, bulkUpdatePrices, getStore } from "@/lib/api";
import { Package, Save, ArrowLeft, Boxes, Check } from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Manual initial inventory capture — cantidad por producto.
 * Reutiliza el endpoint bulk-prices (que ya acepta stock_quantity opcional).
 * Voz DESACTIVADA aquí — captura por teclado. Después la voz reajusta stock.
 */
export default function InventarioInicial() {
  const nav = useNavigate();
  const store = getStore();
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);

  useEffect(() => {
    if (!store?.id) return nav("/setup");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listProducts();
      setProducts(list);
      setStocks({});
    } catch {
      toast.error("No se pudo cargar el inventario");
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const counts = {};
    products.forEach((p) => { counts[p.category || "General"] = (counts[p.category || "General"] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [products]);

  const filtered = useMemo(() => {
    let list = products;
    if (category) list = list.filter((p) => (p.category || "General") === category);
    if (onlyMissing) list = list.filter((p) => (p.stock_quantity ?? 0) <= 0);
    return list;
  }, [products, category, onlyMissing]);

  const totalMissing = useMemo(
    () => products.filter((p) => (p.stock_quantity ?? 0) <= 0).length,
    [products]
  );

  const setStock = (id, value) => setStocks((s) => ({ ...s, [id]: value }));

  const withValues = useMemo(
    () => Object.entries(stocks).filter(([, v]) => v !== "" && v !== undefined && Number(v) >= 0),
    [stocks]
  );

  const save = async () => {
    if (withValues.length === 0) return toast.error("Escribe al menos una cantidad");
    setSaving(true);
    try {
      const items = withValues.map(([id, v]) => {
        const prod = products.find((p) => p.id === id);
        return {
          id,
          sale_price: prod?.sale_price || 0,
          stock_quantity: Number(v),
        };
      });
      const r = await bulkUpdatePrices(items);
      toast.success(`${r.updated} ${r.updated === 1 ? "cantidad guardada" : "cantidades guardadas"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar las cantidades");
    } finally {
      setSaving(false);
    }
  };

  const focusNext = (e, idx) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const next = document.querySelector(`[data-testid="stock-input-${idx + 1}"]`);
    if (next) next.focus();
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="inventario-inicial">
      <DashNav />
      <div className="grain-overlay" />
      <Toaster position="top-center" theme="dark" richColors />

      <div className="mx-auto max-w-6xl px-6 md:px-10 pt-14 pb-40 relative z-10">
        <button
          onClick={() => nav("/dashboard")}
          className="flex items-center gap-1.5 text-white/50 hover:text-white text-[10px] font-mono uppercase tracking-widest mb-4"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={12} /> Regresar al panel
        </button>

        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">
              / Inventario Inicial
            </p>
            <h1 className="font-satoshi text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Dinos <span className="text-[#CCFF00]">cuántas tienes.</span>
            </h1>
            <p className="text-white/60 mt-4 max-w-2xl text-base">
              Anota la existencia inicial de cada producto que hoy tienes en tu tienda. Recorre categoría por categoría.{" "}
              <span className="text-white/40">
                Al terminar, Chalán irá restando automáticamente cada venta y avisándote cuando algo se te esté acabando.
              </span>
            </p>
          </div>
          <div className="hidden md:flex flex-col items-end gap-1 shrink-0">
            <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Faltan</span>
            <span className="font-satoshi text-4xl text-[#FF5A00] leading-none" data-testid="stock-pending-count">
              {totalMissing}
            </span>
            <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase">sin cantidad</span>
          </div>
        </div>

        <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 flex items-start gap-3">
          <Boxes size={18} className="text-[#CCFF00] shrink-0 mt-0.5" />
          <div className="text-sm text-white/70">
            <strong className="text-white">Captura con teclado. Guarda antes de salir.</strong> Escribe la cantidad
            que hoy tienes en piso, refri y bodega — Chalán la irá descontando venta por venta. Recuerda tocar{" "}
            <em className="text-[#CCFF00]">Guardar existencias</em> antes de cerrar la pantalla.
          </div>
        </div>

        {categories.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase mr-2">Categoría:</span>
            <button
              onClick={() => setCategory("")}
              className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition ${!category ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:text-white"}`}
              data-testid="stock-filter-all"
            >
              Todas · {products.length}
            </button>
            {categories.slice(0, 12).map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition ${category === cat ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:text-white"}`}
                data-testid={`stock-filter-${cat}`}
              >
                {cat} · {count}
              </button>
            ))}
            <label className="ml-4 flex items-center gap-2 cursor-pointer text-white/60 hover:text-white text-[11px] font-mono uppercase tracking-widest">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
                className="accent-[#CCFF00]"
                data-testid="toggle-only-missing"
              />
              Solo sin cantidad
            </label>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="empty-stock">
            <Check size={40} className="mx-auto mb-4 text-[#CCFF00]" />
            <h2 className="font-satoshi text-2xl mb-2">Todo con cantidad · listo para vender</h2>
            <p className="text-white/50 text-sm mb-6">
              Cada venta se descontará automáticamente. Puedes seguir subiendo stock por voz cuando llegue mercancía.
            </p>
            <button
              onClick={() => nav("/dashboard")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest"
              data-testid="go-dashboard-done"
            >
              Ir al panel
            </button>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="rounded-3xl border border-white/10 overflow-hidden bg-white/[0.02]" data-testid="stock-table">
            <div
              className="grid gap-3 px-5 py-3 border-b border-white/10 bg-white/[0.03] font-mono text-[10px] tracking-widest uppercase text-white/40"
              style={{ gridTemplateColumns: `1fr 90px 130px` }}
            >
              <span>Producto</span>
              <span className="text-right">Unidad</span>
              <span className="text-right">Cantidad inicial</span>
            </div>
            <div className="divide-y divide-white/5">
              {filtered.map((p, i) => {
                const current = stocks[p.id];
                const done = current !== undefined && current !== "" && Number(current) >= 0;
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.008, 0.4) }}
                    className={`grid gap-3 px-5 py-3 items-center ${done ? "bg-[#CCFF00]/[0.03]" : ""}`}
                    style={{ gridTemplateColumns: `1fr 90px 130px` }}
                    data-testid={`stock-row-${i}`}
                  >
                    <div className="min-w-0">
                      <p className="font-satoshi text-sm truncate">{p.name}</p>
                      <p className="font-mono text-[9px] tracking-widest text-white/40 uppercase">
                        {p.category} {p.brand ? `· ${p.brand}` : ""} {(p.stock_quantity ?? 0) > 0 ? `· hoy: ${p.stock_quantity}` : ""}
                      </p>
                    </div>
                    <p className="text-right font-mono text-[10px] tracking-widest text-white/50 uppercase">
                      {p.unit || "pza"}
                    </p>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="1"
                      min="0"
                      placeholder="0"
                      value={current ?? ""}
                      onChange={(e) => setStock(p.id, e.target.value)}
                      onKeyDown={(e) => focusNext(e, i)}
                      className={`w-full px-3 py-2 rounded-lg border text-right font-satoshi text-sm bg-black/40 focus:outline-none focus:ring-1 ${done ? "border-[#CCFF00]/40 text-[#CCFF00] focus:ring-[#CCFF00]" : "border-white/10 text-white focus:ring-white/40"}`}
                      data-testid={`stock-input-${i}`}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-white/40 font-mono text-xs uppercase tracking-widest">
            Cargando inventario…
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-black/85 backdrop-blur-lg z-40" data-testid="stock-savebar">
          <div className="mx-auto max-w-6xl px-6 md:px-10 py-4 flex items-center justify-between gap-4">
            <div className="text-sm">
              <span className="font-satoshi text-2xl text-white" data-testid="stock-ready-count">
                {withValues.length}
              </span>
              <span className="text-white/50 ml-2 font-mono text-[10px] uppercase tracking-widest">
                {withValues.length === 1 ? "cantidad lista" : "cantidades listas"}
              </span>
            </div>
            <button
              onClick={save}
              disabled={saving || withValues.length === 0}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest disabled:opacity-40"
              data-testid="save-stock-btn"
            >
              <Save size={14} /> {saving ? "Guardando…" : "Guardar existencias"}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
